import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, LogOut } from 'lucide-react';
import { cn } from '../../lib/utils';
import { getMobileNavItems, type TabId } from './navigationConfig';
import { createPortal } from 'react-dom';
import { useAuth } from '../../hooks/useAuth';
import { useAppSelector } from '../../store';

interface MobileBottomTabsProps {
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
  isAuthenticated?: boolean;
  isAdmin?: boolean;
  className?: string;
}

/**
 * MobileBottomTabs - Native-style mobile bottom navigation
 *
 * Features:
 * - Fixed bottom position like native apps
 * - iOS-style blur background
 * - Primary tabs with icons and labels (horizontally scrollable when > 4)
 * - Touch-friendly (56px min targets)
 * - Safe area inset support
 * - Hidden on desktop (md:hidden)
 * - Admin/Agent tabs shown for admin users
 * - Horizontal scroll when many tabs
 * - Native app-like styling with rounded active state
 * - Visual scroll hint when content overflows
 */
const MobileBottomTabs: React.FC<MobileBottomTabsProps> = React.memo(
  ({
    activeTab,
    onTabChange,
    isAuthenticated = false,
    isAdmin = false,
    className,
  }) => {
    const { t, i18n } = useTranslation();
    const { logout, user } = useAuth();
    const [showLanguageMenu, setShowLanguageMenu] = useState(false);
    const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
    const languageButtonRef = useRef<HTMLButtonElement>(null);
    const languageMenuRef = useRef<HTMLDivElement>(null);
    // Re-render when the feature-status probe settles so Trade Diary nav appears.
    const positionDiaryFeatureEnabled = useAppSelector(
      (s) => s.positions.featureEnabled
    );
    void positionDiaryFeatureEnabled;
    const mobileNavItems = getMobileNavItems(isAuthenticated, isAdmin);

    // BUG-001: discoverable scroll affordance - fade the edge(s) that have more
    // off-screen nav items so users know the bar scrolls horizontally.
    const scrollRef = useRef<HTMLDivElement>(null);
    const [edges, setEdges] = useState({ start: false, end: false });
    useEffect(() => {
      const el = scrollRef.current;
      if (!el) return;
      const update = () => {
        const { scrollLeft, scrollWidth, clientWidth } = el;
        const max = scrollWidth - clientWidth;
        // Use abs() so it works in RTL (where scrollLeft is negative).
        const sl = Math.abs(scrollLeft);
        setEdges({ start: sl > 4, end: sl < max - 4 });
      };
      update();
      el.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      return () => {
        el.removeEventListener('scroll', update);
        window.removeEventListener('resize', update);
      };
    }, [mobileNavItems.length]);

    const handleLanguageChange = (lang: string) => {
      i18n.changeLanguage(lang);
      setShowLanguageMenu(false);
    };

    const handleSignOut = async () => {
      setShowLanguageMenu(false);
      try {
        await logout();
      } catch (err) {
        console.error('[MobileBottomTabs] logout failed:', err);
      }
    };

    useEffect(() => {
      if (!showLanguageMenu) return;

      const updateMenuPosition = () => {
        const trigger = languageButtonRef.current;
        if (!trigger) return;
        const rect = trigger.getBoundingClientRect();
        setMenuPosition({
          top: rect.top - 8,
          left: rect.left + rect.width / 2,
        });
      };

      const handlePointerDown = (event: MouseEvent | TouchEvent) => {
        const target = event.target as Node | null;
        if (!target) return;
        if (
          languageButtonRef.current?.contains(target) ||
          languageMenuRef.current?.contains(target)
        ) {
          return;
        }
        setShowLanguageMenu(false);
      };

      updateMenuPosition();
      window.addEventListener('resize', updateMenuPosition);
      window.addEventListener('scroll', updateMenuPosition, true);
      document.addEventListener('mousedown', handlePointerDown);
      document.addEventListener('touchstart', handlePointerDown);

      return () => {
        window.removeEventListener('resize', updateMenuPosition);
        window.removeEventListener('scroll', updateMenuPosition, true);
        document.removeEventListener('mousedown', handlePointerDown);
        document.removeEventListener('touchstart', handlePointerDown);
      };
    }, [showLanguageMenu]);

    const languageDropdown =
      showLanguageMenu && menuPosition
        ? createPortal(
            <div
              ref={languageMenuRef}
              className={cn(
                'fixed z-[70] min-w-[120px] overflow-hidden rounded-xl',
                'bg-surface-container/95 backdrop-blur-xl',
                'border border-outline-variant/20 shadow-lg shadow-black/30'
              )}
              style={{
                top: menuPosition.top,
                left: menuPosition.left,
                transform: 'translate(-50%, -100%)',
              }}
            >
              <button
                onClick={() => handleLanguageChange('en')}
                className={cn(
                  'w-full flex items-center justify-center px-4 py-2 text-sm',
                  'text-text-secondary hover:text-text-primary hover:bg-white/5',
                  'transition-colors duration-200',
                  (i18n.language === 'en' || i18n.language?.startsWith('en')) &&
                    'text-accent-primary bg-accent-primary/10'
                )}
              >
                English
              </button>
              <button
                onClick={() => handleLanguageChange('he')}
                className={cn(
                  'w-full flex items-center justify-center px-4 py-2 text-sm',
                  'text-text-secondary hover:text-text-primary hover:bg-white/5',
                  'transition-colors duration-200',
                  i18n.language?.startsWith('he') &&
                    'text-accent-primary bg-accent-primary/10'
                )}
              >
                עברית
              </button>
              <button
                onClick={() => handleLanguageChange('ar')}
                className={cn(
                  'w-full flex items-center justify-center px-4 py-2 text-sm',
                  'text-text-secondary hover:text-text-primary hover:bg-white/5',
                  'transition-colors duration-200',
                  i18n.language?.startsWith('ar') &&
                    'text-accent-primary bg-accent-primary/10'
                )}
              >
                العربية
              </button>
              <button
                onClick={() => handleLanguageChange('ru')}
                className={cn(
                  'w-full flex items-center justify-center px-4 py-2 text-sm',
                  'text-text-secondary hover:text-text-primary hover:bg-white/5',
                  'transition-colors duration-200',
                  i18n.language?.startsWith('ru') &&
                    'text-accent-primary bg-accent-primary/10'
                )}
              >
                Русский
              </button>
              {isAuthenticated && user && (
                <>
                  <div
                    className="h-px bg-outline-variant/20 mx-2"
                    aria-hidden="true"
                  />
                  <button
                    onClick={handleSignOut}
                    dir="ltr"
                    className={cn(
                      'w-full flex items-center justify-center gap-2 px-4 py-2 text-sm',
                      'text-red-400 hover:text-red-300 hover:bg-red-500/10',
                      'transition-colors duration-200',
                    )}
                    aria-label={t('common.signOut', { defaultValue: 'Sign out' })}
                  >
                    <LogOut className="h-4 w-4" />
                    {t('common.signOut', { defaultValue: 'Sign out' })}
                  </button>
                </>
              )}
            </div>,
            document.body
          )
        : null;

    return (
      <nav
        className={cn(
          'fixed bottom-0 left-0 right-0 z-50',
          // Institutional Editor: glass panel with surface background
          'bg-surface/95 backdrop-blur-xl',
          // No-line: tonal shift instead of border
          'border-t border-transparent',
          'md:hidden', // Hide on desktop
          className
        )}
        style={{
          paddingBottom: 'env(safe-area-inset-bottom, 8px)',
          boxShadow: '0 -4px 24px rgba(0,0,0,0.4)',
        }}
        aria-label="Mobile navigation"
      >
        {/* Edge fades signal that the nav scrolls to reveal more tabs (BUG-001). */}
        {edges.start && (
          <div className="pointer-events-none absolute bottom-0 left-0 top-0 z-10 w-8 bg-gradient-to-r from-surface to-transparent" aria-hidden="true" />
        )}
        {edges.end && (
          <div className="pointer-events-none absolute bottom-0 right-0 top-0 z-10 flex w-8 items-center justify-end bg-gradient-to-l from-surface to-transparent" aria-hidden="true">
            <span className="mr-0.5 text-text-secondary" style={{ fontSize: '14px', lineHeight: 1 }}>›</span>
          </div>
        )}
        <div
          ref={scrollRef}
          role="region"
          aria-label="Mobile navigation tabs"
          className={cn(
            'flex items-end justify-start py-2 px-2 gap-1',
            // Horizontal scroll when many items
            'overflow-x-auto hide-scrollbar scroll-smooth'
          )}
        >
          {mobileNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;

            return (
              <button
                key={item.id}
                onClick={() => onTabChange(item.id as TabId)}
                className={cn(
                  'flex flex-col items-center justify-center gap-0.5',
                  'py-2 px-2',
                  'min-h-[56px] shrink-0',
                  // Allow tabs to grow when few items, but shrink when many
                  mobileNavItems.length <= 4
                    ? 'flex-1 max-w-[90px]'
                    : 'w-[64px]',
                  'transition-all duration-200 ease-in-out',
                  'relative rounded-xl',
                  isActive
                    ? 'bg-accent-primary/15 text-accent-primary'
                    : 'text-text-secondary hover:text-text-primary active:bg-white/5'
                )}
                aria-current={isActive ? 'page' : undefined}
                aria-label={t(item.labelKey)}
                title={item.beta ? t('nav.betaTooltip', { defaultValue: 'Beta feature - may still contain bugs.' }) : undefined}
              >
                <Icon
                  className={cn(
                    'h-6 w-6',
                    isActive && 'drop-shadow-[0_0_8px_rgba(242,202,80,0.5)]'
                  )}
                />
                <span
                  className={cn(
                    'text-[10px] font-medium leading-tight truncate w-full text-center whitespace-nowrap overflow-hidden',
                    isActive ? 'text-accent-primary' : 'text-text-secondary'
                  )}
                >
                  {t(item.mobileLabelKey || item.labelKey)}
                </span>
                {isActive && (
                  <span
                    className="absolute top-1.5 left-1/2 -translate-x-1/2 w-6 h-0.5 rounded-full bg-accent-primary"
                    aria-hidden="true"
                  />
                )}
                {item.beta && (
                  <span className="absolute top-1 end-1 h-1.5 w-1.5 rounded-full bg-accent-primary" aria-hidden="true" title={t('nav.betaTooltip', { defaultValue: 'Beta feature - may still contain bugs.' })} />
                )}
              </button>
            );
          })}

          {/* Language Selector */}
          <div className="relative">
            <button
              ref={languageButtonRef}
              onClick={() => setShowLanguageMenu(!showLanguageMenu)}
              className={cn(
                'flex flex-col items-center justify-center gap-0.5',
                'py-2 px-2',
                'min-h-[56px] shrink-0 w-[56px]',
                'transition-all duration-200 ease-in-out',
                'relative rounded-xl',
                showLanguageMenu
                  ? 'bg-accent-primary/15 text-accent-primary'
                  : 'text-text-secondary hover:text-text-primary active:bg-white/5'
              )}
              aria-label={t('language.selectLanguage')}
            >
              <Globe className="h-6 w-6" />
              <span
                className={cn(
                  'text-[11px] font-medium leading-tight truncate max-w-[40px]',
                  showLanguageMenu ? 'text-accent-primary' : 'text-text-secondary'
                )}
              >
                {i18n.language?.startsWith('he')
                  ? 'HE'
                  : i18n.language?.startsWith('ar')
                    ? 'AR'
                    : i18n.language?.startsWith('ru')
                      ? 'RU'
                      : 'EN'}
              </span>
            </button>
          </div>

        </div>
        {languageDropdown}
      </nav>
    );
  }
);

MobileBottomTabs.displayName = 'MobileBottomTabs';

export default MobileBottomTabs;
